import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    BarChart3,
    Banknote,
    CreditCard,
    List as ListIcon,
    Minus,
    QrCode,
    Receipt,
    TrendingDown,
    TrendingUp,
    Eye,
    Package,
    Percent,
    Wallet,
    XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import {
    getSaleRowFinalTotal,
    getSaleRowGrossAmount,
    getSaleRowItemDiscount,
} from '../../common/saleUtils';
import { FilterChips } from '../../component/filter/FilterToolbar';
import ReportFilterBar from '../../component/report/ReportFilterBar';
import DialogInfo from '../../component/dialog/DialogInfo';
import StatusChip from '../../component/common/StatusChip';
import KpiTickerCard from '../../component/report/KpiTickerCard';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import SaleViewSheet from '../../component/sheet/SaleViewSheet';
import DefaultTable from '../../component/table/DefaultTable';
import '../reports/reports.css';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { parseMoney } from '../../component/common/MobileDashboardKit';
import { KitField, KitSelect } from '../../component/common/MobileKitExtras';
import { protoIcon } from '../../component/common/ProtoIcon';
import MobileReport, { MobileReportBody, formatMoneyKpi, formatMoneyRank, bucketByPeriod, useMobileRegenerate } from '../../component/report/MobileReport';
import { AreaLine, BarsChart, Caption, ChartCard, DonutBlock, HBarsLimited, SalesList, SeriesLegend, YearSelect } from '../../component/report/MobileSalesSections';
import { KitSeg } from '../../component/common/MobileKitExtras';
import { KitEmptyNote } from '../../component/common/MobileDashboardKit';

/**
 * Coluna "Total c/ Desconto" — `getSaleRowFinalTotal` (compartilhado por
 * `saleUtils.js`) prioriza `total_discount`/`total_with_discount`, e o
 * payload de `GET /chart/sale` (`SaleData` em `fv-api/report/sale.go`) só
 * tem o segundo: `total_with_discount` é **intencionalmente** "0,00" sempre
 * que a venda não teve desconto (comentário no próprio struct do backend),
 * então toda venda sem desconto aparecia como "0,00" em vez do total pago —
 * diferente do protótipo, que mostra `Valor total − Desc. itens`. O mesmo
 * `SaleData` sempre preenche `payable_total` ("valor efetivo da venda, a
 * pagar, sempre preenchido") — é esse o campo que bate com a aritmética do
 * protótipo, então esta tela usa `payable_total` primeiro e só cai para o
 * helper compartilhado se o payload não trouxer esse campo (outro formato de
 * `row`). Fix local a este arquivo — `saleUtils.js` não foi alterado porque
 * outras telas (`pages/sales/List.js`, `sales/View.js`) já dependem do
 * comportamento atual de `getSaleRowFinalTotal` para o próprio payload delas.
 */
const getSaleRowPayableTotal = (row) => {
    const payable = row?.payable_total ?? row?.PayableTotal ?? row?.payableTotal;
    if (payable !== undefined && payable !== null && payable !== '') return payable;
    return getSaleRowFinalTotal(row);
};

const formatBrl = (v) =>
    Number(v ?? 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

// Rótulo compacto para o eixo Y dos gráficos de barra em R$ (ex. "R$ 6,2k")
// — `formatBrl` sozinho estoura a largura do eixo e trunca ("R$ 6...").
const formatCompactCurrency = (value) => {
    if (value === null || value === undefined) return '';
    const abs = Math.abs(value);
    if (abs >= 1000) {
        return `R$ ${(value / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}k`;
    }
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

/** @returns {string|null} texto tipo "+25,86%" ou null se não houver base */
const formatPctDelta = (pct) => {
    if (pct == null || Number.isNaN(Number(pct))) return null;
    const n = Number(pct);
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};

const profitYoYPercentage = (payload) => {
    if (
        payload?.previous_total_sales == null
        || payload?.previous_total_costs == null
    ) return null;
    const prev = Number(payload.previous_total_sales) - Number(payload.previous_total_costs);
    const curr = Number(
        payload.profit ?? (Number(payload.total_sales) - Number(payload.total_costs))
    );
    if (prev === 0) {
        if (curr === 0) return 0;
        return null;
    }
    return ((curr - prev) / Math.abs(prev)) * 100;
};

/**
 * DESIGN-SYSTEM.md §6.1 "report-toolbar" micro-label — uppercase, static,
 * rendered outside the field (not `InputLabel`). Same local pattern already
 * used by `SaleDashboard.js`/`OrderDashboard.js`/`pages/sales/List.js`.
 */
const TbLabel = ({ children }) => (
    <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
        {children}
    </Typography>
);

const TB_FIELD_RADIUS = '9px';
const selectFieldSx = { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS };

// Domínio de forma de pagamento — os valores crus vindos da API (e usados no
// filtro client-side de `getTableData`) são os mesmos enums do backend
// (`DINHEIRO`/`DÉBITO`/`CRÉDITO`/`PIXKEY`/`PIXQRCODE`, ver
// `fv-api/report/sale.go`). `StatusChip domain="paymentMethod"` já cobre
// dinheiro/débito/crédito/pix (DESIGN-SYSTEM.md §4) — só falta reduzir
// PIXKEY/PIXQRCODE a "pix" antes de repassar o valor.
const PIX_RAW_TYPES = new Set(['PIXKEY', 'PIXQRCODE']);
const PAYMENT_FRIENDLY_LABEL = {
    DINHEIRO: 'Dinheiro',
    'DÉBITO': 'Débito',
    'CRÉDITO': 'Crédito',
    PIXKEY: 'Pix (Chave)',
    PIXQRCODE: 'Pix (QR Code)',
};
const paymentDomainValue = (raw) => (PIX_RAW_TYPES.has(raw) ? 'pix' : raw);
const paymentFriendlyLabel = (raw) => PAYMENT_FRIENDLY_LABEL[raw] || raw;

/** Mesmo lookup de tom que `StatusChip domain="paymentMethod"` usa internamente
 * (DESIGN-SYSTEM.md §4), reaproveitado aqui para colorir o donut/gráficos de
 * pagamento com as mesmas cores dos chips da tabela. */
function getPaymentTone(theme, raw) {
    const key = paymentDomainValue(raw);
    switch (key) {
        case 'DINHEIRO':
            return { fg: theme.palette.success.main, bg: theme.palette.success.bg };
        case 'DÉBITO':
            return { fg: theme.palette.info.main, bg: theme.palette.info.bg };
        case 'CRÉDITO':
            return { fg: theme.palette.secondary.main, bg: theme.palette.secondary.bg };
        case 'pix':
            return { fg: theme.palette.brand2.main, bg: theme.palette.info.bg };
        default:
            return { fg: theme.palette.text.disabled, bg: theme.palette.background.subtle };
    }
}

// Ícone por forma de pagamento — mesmo par ícone+cor de `.pay-ic.dinheiro
// /.debito/.credito/.pix` em `prototipo-relatorios.html`.
const PAYMENT_ICON = {
    DINHEIRO: Banknote,
    'DÉBITO': CreditCard,
    'CRÉDITO': CreditCard,
    pix: QrCode,
};

// `.pay-chip` — ícone 22px tintado (`.pay-ic`) + rótulo, DESIGN-SYSTEM.md
// não define esse padrão em §5 (é específico desta tela/protótipo), então
// fica local aqui em vez de virar mais uma variante do `StatusChip`.
function PaymentChip({ raw }) {
    const theme = useTheme();
    const tone = getPaymentTone(theme, raw);
    const Icon = PAYMENT_ICON[paymentDomainValue(raw)] || Banknote;
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Box sx={{
                width: 22,
                height: 22,
                borderRadius: '7px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: tone.bg,
                color: tone.fg,
            }}>
                <Icon size={12} strokeWidth={2} />
            </Box>
            <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary' }}>
                {paymentFriendlyLabel(raw)}
            </Typography>
        </Box>
    );
}

/** Mesma técnica de `SaleDashboard.js`'s `getStatusTone` — normaliza o texto
 * (minúsculo, sem acento) vindo da API e resolve para os tons semânticos do
 * tema, com fallback neutro para qualquer valor inesperado. */
function getSaleStatusTone(theme, label) {
    const key = (label || '').toLowerCase();
    if (key === 'finalizada') return theme.palette.success.main;
    if (key === 'cancelada' || key === 'estornada') return theme.palette.error.main;
    if (key === 'pendente' || key === 'enviada' || key === 'iniciada') return theme.palette.warning.main;
    return theme.palette.text.disabled;
}

const fmtBp = (bp) => `${((bp || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

// "Mix por forma de pagamento" — ref. `ui-ux/prototipo-relatorio-vendas-kpis.html`:
// barra empilhada + valor e % de cada forma. Os 4 totais vêm de `/chart/sale`
// (Dinheiro/Débito/Crédito/Pix); o total e os % são calculados sobre a soma deles.
function MixPanel({ items, loading }) {
    const total = items.reduce((acc, it) => acc + it.value, 0);
    const brl = (v, digits = 0) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: '14px 18px', '&:last-child': { pb: '14px' } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: '10px' }}>
                    <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'text.primary' }}>Forma de pagamento</Typography>
                        <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>Participação no faturamento líquido do período</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', height: 12, borderRadius: '8px', overflow: 'hidden', bgcolor: 'background.subtle', gap: '2px' }}>
                    {!loading && total > 0 && items.map((it) => (
                        <Box key={it.key} sx={{ height: '100%', width: `${(it.value / total) * 100}%`, bgcolor: it.color, transition: 'width .3s' }} />
                    ))}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: '12px', mt: '12px' }}>
                    {items.map((it) => (
                        <Box key={it.key} sx={{ display: 'flex', flexDirection: 'column', gap: '2px', fontVariantNumeric: 'tabular-nums' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', fontWeight: 600, color: 'text.secondary' }}>
                                <Box component="i" sx={{ width: 9, height: 9, borderRadius: '3px', display: 'inline-block', bgcolor: it.color }} />
                                {it.label}
                            </Box>
                            {loading ? (
                                <Skeleton variant="text" width={90} height={22} />
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: '15px', fontWeight: 800, color: 'text.primary' }}>{brl(it.value, 2)}</Typography>
                                    <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>
                                        {total > 0 ? Math.round((it.value / total) * 100) : 0}% do faturamento
                                    </Typography>
                                </>
                            )}
                        </Box>
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
}

// Card "Total Bruto e Total Líquido" — exibido ao lado do MixPanel (70/30).
function GrossNetPanel({ loading, totalSales = 0, totalGross = 0, totalNet = 0 }) {
    const brl = (v) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: '14px 18px', '&:last-child': { pb: '14px' }, height: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', minWidth: { md: '170px' }, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em' }}>Total de vendas</Typography>
                        {loading ? <Skeleton variant="text" width={50} height={26} /> : (
                            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary' }}>{Number(totalSales).toLocaleString('pt-BR')}</Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em' }}>Total Bruto</Typography>
                        {loading ? <Skeleton variant="text" width={110} height={26} /> : (
                            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary' }}>{brl(totalGross)}</Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'primary.main', textTransform: 'uppercase', letterSpacing: '.05em' }}>Total Líquido</Typography>
                        {loading ? <Skeleton variant="text" width={110} height={26} /> : (
                            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: 'primary.main' }}>{brl(totalNet)}</Typography>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

const YoYDeltaRow =({ pct, previousYear, dense }) => {
    const text = formatPctDelta(pct);
    const n = pct == null || Number.isNaN(Number(pct)) ? null : Number(pct);
    const iconProps = { size: dense ? 12 : 13, strokeWidth: 2.5, style: { verticalAlign: 'middle', marginRight: 2 } };

    if (previousYear == null || text == null) {
        return (
            <Typography variant="caption" sx={{ display: 'block', mt: dense ? 0.125 : 0.25, color: 'text.disabled' }}>
                Sem comparação com o ano anterior
            </Typography>
        );
    }

    let Icon = Minus;
    let color = 'text.secondary';
    if (n > 0) {
        Icon = TrendingUp;
        color = 'success.main';
    } else if (n < 0) {
        Icon = TrendingDown;
        color = 'error.main';
    }

    return (
        <Typography
            variant="caption"
            component="div"
            sx={{
                mt: dense ? 0.125 : 0.25,
                fontWeight: 700,
                color,
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                flexWrap: 'wrap'
            }}
        >
            <Icon {...iconProps} />
            <span>{text}</span>
            <Typography component="span" variant="caption" sx={{ fontWeight: 500, color: 'text.disabled' }}>
                vs {previousYear}
            </Typography>
        </Typography>
    );
};

// DESIGN-SYSTEM.md §5.11 empty state (variante compacta, dentro de um
// painel de gráfico) — mesmo componente local já usado por
// `SaleDashboard.js`/`OrderDashboard.js`.
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
            }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

// `.legend-item` — dot + label + valor em negrito, usado sob os donuts.
function LegendRow({ color, label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '11.5px', color: 'text.secondary', width: '100%' }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
            <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
            <Typography component="span" sx={{ fontSize: '11.5px', fontWeight: 700, color: 'text.primary', ml: 'auto' }}>{value}</Typography>
        </Box>
    );
}

// Painel de gráfico genérico (`.panel-head` sem ícone — igual ao protótipo,
// diferente dos cards de KPI/página que têm ícone tintado).
function ChartPanel({ title, subtitle, headerActions, loading, children }) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                        {loading ? (
                            <>
                                <Skeleton variant="text" width={160} height={20} />
                                <Skeleton variant="text" width={200} height={14} />
                            </>
                        ) : (
                            <>
                                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
                                {subtitle ? (
                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>{subtitle}</Typography>
                                ) : null}
                            </>
                        )}
                    </Box>
                    {headerActions ? <Box sx={{ flexShrink: 0 }}>{headerActions}</Box> : null}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {children}
                </Box>
            </CardContent>
        </Card>
    );
}

// Gráfico anual Vendas x Custos (GET /dashboard/sales-vs-costs) — DESIGN-SYSTEM.md
// §5.9 "Stacked bar (Vendas x Custos) | BarChart with two series".
const SalesVsCostsYearChart = ({ year, onYearChange, payload, loading, errorMessage }) => {
    const theme = useTheme();
    const chartDataset = payload?.data ?? [];
    const previousYear = payload?.previous_year;
    const profitPct = useMemo(
        () => (payload && !errorMessage ? profitYoYPercentage(payload) : null),
        [payload, errorMessage]
    );
    const yearOptions = useMemo(() => {
        const y = dayjs().year();
        return Array.from({ length: 12 }, (_, i) => y - i);
    }, []);

    const yearSelect = (
        <FormControl size="small" sx={{ minWidth: 96 }}>
            <Select
                value={year}
                onChange={(e) => onYearChange(Number(e.target.value))}
                displayEmpty
                sx={selectFieldSx}
            >
                {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    return (
        <ChartPanel
            title="Vendas x Custos por mês"
            subtitle="Vendas finalizadas e custo dos itens no ano (jan–dez)"
            headerActions={yearSelect}
            loading={loading}
        >
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                    <Stack direction="row" flexWrap="wrap" gap={2}>
                        {[1, 2, 3].map((k) => (
                            <Skeleton key={k} variant="rounded" height={64} sx={{ flex: '1 1 160px' }} />
                        ))}
                    </Stack>
                    <Skeleton variant="rounded" width="100%" height={260} sx={{ flex: 1 }} />
                </Box>
            ) : errorMessage ? (
                <Typography sx={{ fontSize: '13px', color: 'error.main' }}>{errorMessage}</Typography>
            ) : payload ? (
                <>
                    {previousYear != null && (
                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', display: 'block', mb: 1.5 }}>
                            Percentuais de variação em relação a <strong>{previousYear}</strong>
                            {payload.year != null ? <> (ano selecionado: <strong>{payload.year}</strong>)</> : null}
                        </Typography>
                    )}
                    <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                        <Box sx={{ flex: '1 1 160px', p: 1.5, borderRadius: '10px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.subtle' }}>
                            <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase' }}>
                                Vendas no ano
                            </Typography>
                            <Typography sx={{ fontSize: '17px', fontWeight: 800, color: 'text.primary', mt: 0.5 }}>
                                {formatBrl(payload.total_sales)}
                            </Typography>
                            <YoYDeltaRow pct={payload.sales_growth_percentage} previousYear={previousYear} />
                        </Box>
                        <Box sx={{ flex: '1 1 160px', p: 1.5, borderRadius: '10px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.subtle' }}>
                            <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase' }}>
                                Custos no ano
                            </Typography>
                            <Typography sx={{ fontSize: '17px', fontWeight: 800, color: 'text.primary', mt: 0.5 }}>
                                {formatBrl(payload.total_costs)}
                            </Typography>
                            <YoYDeltaRow pct={payload.costs_growth_percentage} previousYear={previousYear} />
                        </Box>
                        <Box sx={{ flex: '1 1 160px', p: 1.5, borderRadius: '10px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.subtle' }}>
                            <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase' }}>
                                Lucro bruto
                            </Typography>
                            <Typography sx={{ fontSize: '17px', fontWeight: 800, mt: 0.5, color: Number(payload.profit) >= 0 ? 'success.main' : 'error.main' }}>
                                {formatBrl(payload.profit)}
                            </Typography>
                            <YoYDeltaRow pct={profitPct} previousYear={previousYear} />
                        </Box>
                    </Stack>

                    <Box sx={{ height: 260, width: '100%' }}>
                        {chartDataset.length > 0 ? (
                            <BarChart
                                dataset={chartDataset}
                                margin={{ left: 8, right: 8, top: 12, bottom: 0 }}
                                borderRadius={6}
                                xAxis={[{
                                    scaleType: 'band',
                                    dataKey: 'month_label',
                                    categoryGapRatio: 0.5,
                                    disableLine: true,
                                    disableTicks: true,
                                    tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled, textTransform: 'capitalize' }
                                }]}
                                yAxis={[{
                                    disableLine: true,
                                    disableTicks: true,
                                    valueFormatter: (value) => formatBrl(value)
                                }]}
                                series={[
                                    {
                                        dataKey: 'total_sales',
                                        label: 'Vendas',
                                        color: theme.palette.primary.main,
                                        valueFormatter: (value, context) => {
                                            const base = formatBrl(value);
                                            const row = chartDataset[context?.dataIndex];
                                            const p = formatPctDelta(row?.sales_growth_percentage);
                                            if (!p || previousYear == null) return base;
                                            return `${base} (${p} vs ${previousYear})`;
                                        }
                                    },
                                    {
                                        dataKey: 'total_costs',
                                        label: 'Custos',
                                        color: theme.palette.secondary.main,
                                        valueFormatter: (value, context) => {
                                            const base = formatBrl(value);
                                            const row = chartDataset[context?.dataIndex];
                                            const p = formatPctDelta(row?.costs_growth_percentage);
                                            if (!p || previousYear == null) return base;
                                            return `${base} (${p} vs ${previousYear})`;
                                        }
                                    }
                                ]}
                                height={230}
                                sx={{ '& .MuiChartsAxis-directionY': { display: 'none' } }}
                            />
                        ) : (
                            <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem vendas ou custos registrados neste ano" />
                        )}
                    </Box>
                </>
            ) : (
                <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                    Não foi possível exibir o gráfico.
                </Typography>
            )}
        </ChartPanel>
    );
};

// Gráfico de linha/área (Crescimento mensal / Vendas por hora) —
// DESIGN-SYSTEM.md §5.9 "Inline SVG polyline + gradient area | LineChart
// (area variant)", mesma técnica visual de `OrderDashboard.js`.
const AreaLineChart = ({ dataset, xKey, yKey, gradientId, title, subtitle, loading, height = 240, headerActions = null, errorMessage = null, emptyTitle = 'Nenhum dado disponível', emptySubtitle = 'Ajuste o período acima para visualizar os dados' }) => {
    const theme = useTheme();
    const hasData = !errorMessage && Array.isArray(dataset) && dataset.length > 0;

    return (
        <ChartPanel title={title} subtitle={subtitle} headerActions={headerActions} loading={loading}>
            <Box sx={{ height, width: '100%' }}>
                {loading ? (
                    <Skeleton variant="rounded" width="100%" height={height - 20} />
                ) : errorMessage ? (
                    <Typography sx={{ fontSize: '13px', color: 'error.main' }}>{errorMessage}</Typography>
                ) : hasData ? (
                    <LineChart
                        dataset={dataset}
                        height={height - 20}
                        margin={{ top: 16, right: 16, bottom: 24, left: 8 }}
                        hideLegend
                        grid={{ horizontal: true }}
                        xAxis={[{
                            scaleType: 'point',
                            dataKey: xKey,
                            disableLine: true,
                            disableTicks: true,
                            tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                        }]}
                        yAxis={[{ disableLine: true, disableTicks: true }]}
                        series={[{
                            dataKey: yKey,
                            label: title,
                            color: theme.palette.primary.main,
                            area: true,
                            showMark: dataset.length <= 31,
                            valueFormatter: (value, context) => {
                                const secondary = dataset?.[context?.dataIndex]?.SecondaryValues;
                                const base = `${Number(value || 0).toLocaleString('pt-BR')} venda(s)`;
                                return secondary ? `${base} · R$ ${secondary}` : base;
                            }
                        }]}
                        sx={{
                            '& .MuiChartsGrid-line': { stroke: theme.palette.divider },
                            '& .MuiAreaElement-root': { fill: `url(#${gradientId})` },
                            '& .MuiMarkElement-root': {
                                fill: theme.palette.background.paper,
                                stroke: theme.palette.primary.main,
                                strokeWidth: 2,
                            },
                        }}
                    >
                        <defs>
                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.35} />
                                <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                    </LineChart>
                ) : (
                    <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title={emptyTitle} subtitle={emptySubtitle} />
                )}
            </Box>
        </ChartPanel>
    );
};

// Gráfico de barras genérico (contagem ou total em R$ por categoria) —
// mesma detecção automática de `dataKey` (count/total) da versão anterior
// desta tela, só reskinado.
const CategoryBarChart = ({ data, xKey, title, subtitle, loading, height = 240, currency = false }) => {
    const theme = useTheme();
    const dataKey = data && data[0] ? Object.keys(data[0]).find((key) => key === 'count' || key === 'total') : null;

    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            <Box sx={{ height, width: '100%' }}>
                {loading ? (
                    <Skeleton variant="rounded" width="100%" height={height - 20} />
                ) : data && data.length > 0 && dataKey ? (
                    <BarChart
                        dataset={data}
                        height={height - 20}
                        margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
                        borderRadius={6}
                        hideLegend
                        xAxis={[{
                            scaleType: 'band',
                            dataKey: xKey,
                            categoryGapRatio: 0.5,
                            disableLine: true,
                            disableTicks: true,
                            tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                        }]}
                        yAxis={[{
                            disableLine: true,
                            disableTicks: true,
                            width: currency ? 56 : undefined,
                            valueFormatter: currency ? (value) => formatCompactCurrency(value) : undefined,
                        }]}
                        series={[{
                            dataKey,
                            color: theme.palette.primary.main,
                            valueFormatter: (value, context) => {
                                const formattedValue = currency ? formatBrl(value) : `${value} venda(s)`;
                                const secondaryValue = data?.[context?.dataIndex]?.SecondaryValues;
                                return secondaryValue ? `${formattedValue} · ${secondaryValue}` : formattedValue;
                            }
                        }]}
                        sx={{ '& .MuiChartsGrid-line': { stroke: theme.palette.divider } }}
                    />
                ) : (
                    <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem registros no período selecionado" />
                )}
            </Box>
        </ChartPanel>
    );
};

// Donut (PieChart real via @mui/x-charts, nunca conic-gradient à mão —
// DESIGN-SYSTEM.md §5.9) com overlay central + legenda em linhas, mesmo
// padrão de `SaleDashboard.js`.
const DonutPanel = ({ title, subtitle, data, centerValue, centerCaption, loading, emptyTitle, emptySubtitle }) => {
    const hasData = data.some((item) => item.value > 0);
    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <Skeleton variant="circular" width={128} height={128} />
                    <Skeleton variant="text" width="80%" height={16} />
                    <Skeleton variant="text" width="70%" height={16} />
                </Box>
            ) : hasData ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ position: 'relative', width: 128, height: 128 }}>
                        <PieChart
                            series={[{
                                data,
                                innerRadius: 38,
                                outerRadius: 64,
                                paddingAngle: 2,
                                cornerRadius: 3,
                            }]}
                            height={128}
                            width={128}
                            slotProps={{ legend: { hidden: true } }}
                            tooltip={{ trigger: 'item' }}
                            sx={{ '& .MuiChartsLegend-root': { display: 'none' } }}
                        />
                        <Box sx={{
                            position: 'absolute',
                            inset: 18,
                            borderRadius: '50%',
                            bgcolor: 'background.paper',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}>
                            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: 'text.primary', lineHeight: 1.1 }}>
                                {centerValue}
                            </Typography>
                            <Typography sx={{ fontSize: '9.5px', color: 'text.disabled' }}>{centerCaption}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
                        {data.map((item, index) => (
                            <LegendRow key={`${item.label}-${index}`} color={item.color} label={item.label} value={item.displayValue ?? item.value} />
                        ))}
                    </Box>
                </Box>
            ) : (
                <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title={emptyTitle} subtitle={emptySubtitle} />
            )}
        </ChartPanel>
    );
};

const MONTH_ABBR_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/** Monta dataset do gráfico mensal a partir do bloco SalesYear da API (PascalCase ou camelCase). */
const buildSalesYearLineData = (salesYear) => {
    if (!salesYear) return [];
    const values = salesYear.Values ?? salesYear.values;
    if (!Array.isArray(values)) return [];
    const secondary = salesYear.SecondaryValues ?? salesYear.secondaryValues;
    return MONTH_ABBR_LABELS.map((label, index) => ({
        month: label,
        total: values[index] || 0,
        SecondaryValues: Number(secondary?.[index] || 0).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })
    }));
};

export default function SalesReport() {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // Tendência dos KPIs (`POST /chart/sales/kpi-trend`); falha não derruba o relatório.
    const [kpiTrend, setKpiTrend] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month'));
    const [paymentFilter, setPaymentFilter] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [saleViewSheetOpen, setSaleViewSheetOpen] = useState(false);
    const [selectedSaleId, setSelectedSaleId] = useState(null);
    const [salesVsCostsYear, setSalesVsCostsYear] = useState(() => dayjs().year());
    const [salesVsCostsPayload, setSalesVsCostsPayload] = useState(null);
    const [salesVsCostsLoading, setSalesVsCostsLoading] = useState(false);
    const [salesVsCostsError, setSalesVsCostsError] = useState(null);
    const [monthlySalesGrowthYear, setMonthlySalesGrowthYear] = useState(() => dayjs().year());
    const [monthlySalesGrowthSalesYear, setMonthlySalesGrowthSalesYear] = useState(null);
    const [monthlySalesGrowthLoading, setMonthlySalesGrowthLoading] = useState(false);
    const [monthlySalesGrowthError, setMonthlySalesGrowthError] = useState(null);

    const monthlyGrowthYearOptions = useMemo(() => {
        const y = dayjs().year();
        return Array.from({ length: 12 }, (_, i) => y - i);
    }, []);

    const monthlySalesGrowthChartData = useMemo(
        () => buildSalesYearLineData(monthlySalesGrowthSalesYear),
        [monthlySalesGrowthSalesYear]
    );

    useEffect(() => {
        if (activeTab !== 1) return undefined;
        let cancelled = false;
        const load = async () => {
            setSalesVsCostsLoading(true);
            setSalesVsCostsError(null);
            try {
                const response = await httpClient.get('/dashboard/sales-vs-costs', {
                    params: { year: salesVsCostsYear }
                });
                if (cancelled) return;
                if (response.status === 200 && response.data && Array.isArray(response.data.data)) {
                    setSalesVsCostsPayload(response.data);
                } else {
                    const body = response.data;
                    const msg = typeof body === 'string' ? body : (body?.message || 'Erro ao carregar vendas e custos.');
                    setSalesVsCostsPayload(null);
                    setSalesVsCostsError(msg);
                }
            } catch (e) {
                if (!cancelled) {
                    setSalesVsCostsPayload(null);
                    setSalesVsCostsError('Erro ao carregar vendas e custos. Tente novamente.');
                }
            } finally {
                if (!cancelled) {
                    setSalesVsCostsLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [activeTab, salesVsCostsYear]);

    useEffect(() => {
        if (activeTab !== 1) return undefined;
        let cancelled = false;
        const load = async () => {
            setMonthlySalesGrowthLoading(true);
            setMonthlySalesGrowthError(null);
            try {
                const y = monthlySalesGrowthYear;
                const yearStart = dayjs().year(y).startOf('year');
                const yearEnd = dayjs().year(y).endOf('year');
                const response = await httpClient.post('/chart/sale', {
                    start_month: yearStart.format('DD/MM/YYYY'),
                    end_month: yearEnd.format('DD/MM/YYYY'),
                }, {
                    params: { year: y }
                });
                if (cancelled) return;
                const data = response.data;
                const block = data?.SalesYear ?? data?.salesYear;
                const vals = block?.Values ?? block?.values;
                if (response.status === 200 && data && block && Array.isArray(vals)) {
                    setMonthlySalesGrowthSalesYear(block);
                } else {
                    const body = data;
                    const msg = typeof body === 'string' ? body : (body?.message || 'Erro ao carregar crescimento mensal.');
                    setMonthlySalesGrowthSalesYear(null);
                    setMonthlySalesGrowthError(msg);
                }
            } catch (e) {
                if (!cancelled) {
                    setMonthlySalesGrowthSalesYear(null);
                    setMonthlySalesGrowthError('Erro ao carregar crescimento mensal. Tente novamente.');
                }
            } finally {
                if (!cancelled) {
                    setMonthlySalesGrowthLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [activeTab, monthlySalesGrowthYear]);

    // Filtros (mobile): volta aos valores padrão sem gerar o relatório.
    const handleClearFilters = () => {
        setStartDate(dayjs().startOf('month'));
        setEndDate(dayjs().endOf('month'));
        setPaymentFilter('');
    };

    const fetchReportData = async () => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }

        setLoading(true);
        try {
            const trendRequest = httpClient.post('/chart/sales/kpi-trend', {
                start: startDate.format('DD/MM/YYYY'),
                end: endDate.format('DD/MM/YYYY'),
            })
                .then((res) => res.data)
                .catch(() => null);
            const response = await httpClient.post('/chart/sale', {
                start_month: startDate.format('DD/MM/YYYY'),
                end_month: endDate.format('DD/MM/YYYY')
            });

            setKpiTrend(await trendRequest);
            setReportData(response.data);
        } catch (error) {
            console.error('Erro ao buscar dados do relatório:', error);
            setDialogMessage('Erro ao gerar relatório. Tente novamente.');
            setOpenDialog(true);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = (type) => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }

        const url = `/chart/sale/export?start_date=${startDate.format('DD/MM/YYYY')}&end_date=${endDate.format('DD/MM/YYYY')}`;
        httpClient.downloadFile(url, {
            title: `vendas_${startDate.format('DD/MM/YYYY')}_${endDate.format('DD/MM/YYYY')}`,
            extension: 'csv'
        });
    };

    // Preparar dados para gráficos
    const getSalesByPaymentData = () => {
        if (!reportData?.SalesTypePayment) return [];
        return reportData.SalesTypePayment.Labels.map((label, index) => ({
            payment: paymentFriendlyLabel(label),
            count: reportData.SalesTypePayment.Values[index] || 0,
            rawLabel: label,
        }));
    };

    const getSalesByPaymentTotalData = () => {
        if (!reportData?.SalesTotalTypePayment) return [];
        return reportData.SalesTotalTypePayment.Labels.map((label, index) => ({
            payment: paymentFriendlyLabel(label),
            total: reportData.SalesTotalTypePayment.Values[index] || 0,
        }));
    };

    const getSalesPaymentDonutData = () => {
        const rows = getSalesByPaymentData();
        return rows.map((row, index) => {
            const tone = getPaymentTone(theme, row.rawLabel);
            return {
                id: index,
                value: row.count,
                label: row.payment,
                color: tone.fg,
            };
        });
    };

    const getSalesStatusData = () => {
        if (!reportData?.SalesTotalStatus) return [];
        return reportData.SalesTotalStatus.Labels.map((label, index) => ({
            id: index,
            label,
            value: reportData.SalesTotalStatus.Values[index] || 0,
            color: getSaleStatusTone(theme, label),
        }));
    };

    const getSalesByCategoryData = () => {
        if (!reportData?.SalesByCategory) return [];
        return reportData.SalesByCategory.Labels.map((label, index) => ({
            category: label,
            count: reportData.SalesByCategory.Values[index] || 0
        }));
    };

    const getSalesBySubCategoryData = () => {
        if (!reportData?.SalesBySubCategory) return [];
        return reportData.SalesBySubCategory.Labels.map((label, index) => ({
            subcategory: label,
            count: reportData.SalesBySubCategory.Values[index] || 0
        }));
    };

    // Mantida por preservação de comportamento (gráfico "Vendas por Vendedor"
    // já estava desativado/comentado antes deste redesign — ver JSX abaixo).
    const getSalesByUserData = () => {
        if (!reportData?.SaleByUser) return [];
        return reportData.SaleByUser.Labels.map((label, index) => ({
            user: label,
            count: reportData.SaleByUser.Values[index] || 0
        }));
    };

    const getSalesByHourData = () => {
        if (!reportData?.CountSalesByHour) return [];
        return reportData.CountSalesByHour.Labels.map((label, index) => ({
            hour: label,
            count: reportData.CountSalesByHour.Values[index] || 0
        }));
    };

    // Preparar dados para tabela (API pode usar ListSales ou listSales; campos em snake ou PascalCase)
    const getTableData = () => {
        const list =
            reportData?.ListSales ??
            reportData?.listSales ??
            [];
        if (!Array.isArray(list) || list.length === 0) return [];

        let filteredData = list;

        if (paymentFilter) {
            filteredData = list.filter((sale) => {
                if (!sale.type_payment) return false;
                const paymentTypes = sale.type_payment.split(',').map((t) => t.trim());
                return paymentTypes.includes(paymentFilter);
            });
        }

        return filteredData;
    };

    const tableData = getTableData();
    const statusDonutData = getSalesStatusData();
    const totalSalesStatusCount = statusDonutData.reduce((sum, item) => sum + item.value, 0);
    const finalizedCount = statusDonutData.find((item) => item.label === 'Finalizada')?.value || 0;
    const finalizedPct = totalSalesStatusCount > 0 ? Math.round((finalizedCount / totalSalesStatusCount) * 100) : 0;
    const paymentDonutData = getSalesPaymentDonutData();
    const totalPaymentCount = paymentDonutData.reduce((sum, item) => sum + item.value, 0);

    const isMobileShell = useIsMobileShell();
    // Mobile: gera ao abrir e a cada troca de período/filtros (mesma chamada e payload do botão "Gerar").
    const regenerate = useMobileRegenerate(fetchReportData, { enabled: isMobileShell });

    if (isMobileShell) {
        const cancelledCount = statusDonutData.find((item) => (item.label || '').toLowerCase() === 'cancelada')?.value || 0;
        const net = parseMoney(reportData?.TotalAmountNet);
        const ticket = finalizedCount > 0 ? net / finalizedCount : 0;
        const cancelPct = totalSalesStatusCount > 0 ? Math.round((cancelledCount / totalSalesStatusCount) * 100) : 0;
        const evolution = bucketByPeriod(
            tableData.filter((row) => (row.status || '').toLowerCase() === 'finalizada'),
            startDate,
            endDate,
            (row) => dayjs(row.date_created, 'DD/MM/YYYY'),
            (row) => parseMoney(getSaleRowPayableTotal(row))
        );
        const byUser = (reportData?.SaleByUser?.Labels || []).map((name, index) => ({ name, value: Number(reportData.SaleByUser.Values[index]) || 0 }));

        // Mesmos totais do desktop (`KpiCard`s), exibidos por extenso até 99.999,99.
        const moneyKpi = (raw) => {
            const n = parseMoney(raw);
            return Math.abs(n) >= 100000 ? formatMoneyKpi(n) : formatBrl(n);
        };
        const totalKpis = [
            { label: 'Total em Dinheiro', icon: 'dollar', value: moneyKpi(reportData?.TotalAmountSaleCash) },
            { label: 'Total em Débito', icon: 'card', value: moneyKpi(reportData?.TotalAmountSaleDebit) },
            { label: 'Total em Crédito', icon: 'wallet', value: moneyKpi(reportData?.TotalAmountSaleCredit) },
            { label: 'Total em Pix', icon: 'scan', value: moneyKpi(reportData?.TotalAmountSalePixQrCode) },
            { label: 'Total Bruto', icon: 'tag', value: moneyKpi(reportData?.TotalAmountGross) },
            { label: 'Total Líquido', icon: 'check', value: moneyKpi(reportData?.TotalAmountNet) },
        ];

        // Lista: mesma ordem padrão do desktop (número, decrescente).
        const sortedSales = [...tableData].sort((a, b) => (Number(b.number) || 0) - (Number(a.number) || 0));
        const getListRow = (row) => ({
            number: row.number,
            customer: row.customer_name,
            date: row.date_created,
            payments: (row.type_payment?.split(',').map((t) => t.trim()).filter(Boolean) || []).map(paymentFriendlyLabel).join(', '),
            total: formatBrl(parseMoney(getSaleRowPayableTotal(row))),
            status: row.status,
        });

        const hourItems = getSalesByHourData().map((r) => ({ label: r.hour, value: Number(r.count) || 0 }));
        const peakHour = hourItems.reduce((best, it) => (it.value > (best?.value ?? -1) ? it : best), null);
        const growthItems = monthlySalesGrowthChartData.map((r) => ({ label: r.month, value: Number(r.total) || 0, secondary: r.SecondaryValues }));
        const peakMonth = growthItems.reduce((best, it) => (it.value > (best?.value ?? -1) ? it : best), null);
        const costsRows = salesVsCostsPayload?.data ?? [];
        const prevYear = salesVsCostsPayload?.previous_year;
        const capitalize = (t) => (t ? String(t).charAt(0).toUpperCase() + String(t).slice(1) : t);
        const costsProfitPct = salesVsCostsPayload ? profitYoYPercentage(salesVsCostsPayload) : null;
        const deltaCell = (pct) => {
            const text = formatPctDelta(pct);
            if (prevYear == null || text == null) return null;
            const n = Number(pct);
            return (
                <Box component="span" sx={{ display: 'block', fontSize: 11.5, fontWeight: 700, lineHeight: 1.4, color: n > 0 ? 'success.main' : n < 0 ? 'error.main' : 'text.secondary' }}>
                    {text} vs {prevYear}
                </Box>
            );
        };
        const costsSummary = salesVsCostsPayload ? [
            { label: 'Vendas no ano', value: formatBrl(salesVsCostsPayload.total_sales), delta: deltaCell(salesVsCostsPayload.sales_growth_percentage) },
            { label: 'Custos no ano', value: formatBrl(salesVsCostsPayload.total_costs), delta: deltaCell(salesVsCostsPayload.costs_growth_percentage) },
            { label: 'Lucro bruto', value: formatBrl(salesVsCostsPayload.profit), delta: deltaCell(costsProfitPct), color: Number(salesVsCostsPayload.profit) >= 0 ? 'success.main' : 'error.main' },
        ] : [];

        const byCategory = getSalesByCategoryData().map((r) => ({ name: r.category, value: Number(r.count) || 0, text: String(r.count) })).sort((a, b) => b.value - a.value);
        const bySubCategory = getSalesBySubCategoryData().map((r) => ({ name: r.subcategory, value: Number(r.count) || 0, text: String(r.count) })).sort((a, b) => b.value - a.value);
        const byPaymentTotal = getSalesByPaymentTotalData().map((r) => ({ name: r.payment, value: Number(r.total) || 0, text: formatMoneyRank(Number(r.total) || 0) }));
        const segmentsOf = (data) => data.map((item) => ({ value: item.value, color: item.color }));
        const legendOf = (data) => data.map((item) => ({ label: `${item.label} · ${item.value.toLocaleString('pt-BR')}`, color: item.color }));
        const loadingNote = <KitEmptyNote>Carregando...</KitEmptyNote>;

        const chartsNode = (
            <>
                <ChartCard
                    title="Vendas x Custos por mês"
                    subtitle="Vendas finalizadas e custo dos itens no ano (jan–dez)"
                    action={<YearSelect label="Ano de Vendas x Custos" value={salesVsCostsYear} onChange={setSalesVsCostsYear} options={monthlyGrowthYearOptions} />}
                >
                    {salesVsCostsLoading ? loadingNote : salesVsCostsError ? (
                        <Box sx={{ fontSize: 12.5, color: 'error.main', lineHeight: 1.4 }}>{salesVsCostsError}</Box>
                    ) : salesVsCostsPayload ? (
                        <>
                            {costsSummary.map((row) => (
                                <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', mb: '10px' }}>
                                    <Box sx={{ fontSize: 12, color: 'text.disabled', fontWeight: 700, lineHeight: 1.4 }}>{row.label}</Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Box sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.4, color: row.color || 'text.primary' }}>{row.value}</Box>
                                        {row.delta}
                                    </Box>
                                </Box>
                            ))}
                            {costsRows.length > 0 ? (
                                <>
                                    <BarsChart
                                        items={costsRows.map((r) => ({ label: capitalize(r.month_label), value: Number(r.total_sales) || 0 }))}
                                        alt={costsRows.map((r) => ({ value: Number(r.total_costs) || 0 }))}
                                        pairWidth={9}
                                    />
                                    <SeriesLegend items={[{ label: 'Vendas', color: theme.palette.primary.main }, { label: 'Custos', color: theme.palette.secondary.main }]} />
                                </>
                            ) : <KitEmptyNote>Sem vendas ou custos registrados neste ano</KitEmptyNote>}
                        </>
                    ) : <KitEmptyNote>Não foi possível exibir o gráfico.</KitEmptyNote>}
                </ChartCard>

                <ChartCard
                    title="Crescimento mensal de vendas"
                    subtitle={`Ano de ${monthlySalesGrowthYear}`}
                    action={<YearSelect label="Ano do crescimento mensal" value={monthlySalesGrowthYear} onChange={setMonthlySalesGrowthYear} options={monthlyGrowthYearOptions} />}
                >
                    {monthlySalesGrowthLoading ? loadingNote : monthlySalesGrowthError ? (
                        <Box sx={{ fontSize: 12.5, color: 'error.main', lineHeight: 1.4 }}>{monthlySalesGrowthError}</Box>
                    ) : growthItems.length > 0 ? (
                        <>
                            <AreaLine items={growthItems} />
                            {peakMonth && peakMonth.value > 0 && (
                                <Caption>
                                    Maior mês: <strong>{peakMonth.label}</strong> · {peakMonth.value.toLocaleString('pt-BR')} venda(s){peakMonth.secondary ? ` · R$ ${peakMonth.secondary}` : ''}
                                </Caption>
                            )}
                        </>
                    ) : <KitEmptyNote>Nenhum dado disponível</KitEmptyNote>}
                </ChartCard>

                <ChartCard title="Total de Vendas x Hora" subtitle="Distribuição das vendas ao longo do dia">
                    {hourItems.length > 0 ? (
                        <>
                            <AreaLine items={hourItems} />
                            {peakHour && peakHour.value > 0 && (
                                <Caption>Pico: <strong>{peakHour.label}</strong> · {peakHour.value.toLocaleString('pt-BR')} venda(s)</Caption>
                            )}
                        </>
                    ) : <KitEmptyNote>Nenhum dado disponível</KitEmptyNote>}
                </ChartCard>

                <ChartCard title="Vendas x forma de pagamento" bare>
                    <DonutBlock
                        segments={segmentsOf(paymentDonutData)}
                        center={totalPaymentCount.toLocaleString('pt-BR')}
                        legend={legendOf(paymentDonutData)}
                        empty="Sem pagamentos no período selecionado"
                    />
                </ChartCard>

                <ChartCard title="Finalizadas x Canceladas" bare>
                    <DonutBlock
                        segments={segmentsOf(statusDonutData)}
                        center={`${finalizedPct}%`}
                        legend={legendOf(statusDonutData)}
                        empty="Sem vendas no período selecionado"
                    />
                </ChartCard>

                <ChartCard title="Vendas x categoria">
                    {byCategory.length > 0 ? <HBarsLimited items={byCategory} /> : <KitEmptyNote>Sem registros no período selecionado</KitEmptyNote>}
                </ChartCard>

                <ChartCard title="Vendas x subcategoria">
                    {bySubCategory.length > 0 ? <HBarsLimited items={bySubCategory} /> : <KitEmptyNote>Sem registros no período selecionado</KitEmptyNote>}
                </ChartCard>

                <ChartCard title="Total vendas (R$) x forma de pagamento">
                    {byPaymentTotal.length > 0 ? <HBarsLimited items={byPaymentTotal} valueWidth={84} /> : <KitEmptyNote>Sem registros no período selecionado</KitEmptyNote>}
                </ChartCard>

                <ChartCard title="Evolução">
                    {evolution.length > 0 ? <BarsChart items={evolution} /> : <KitEmptyNote>Nenhuma venda no período</KitEmptyNote>}
                </ChartCard>

                <ChartCard title="Por vendedor">
                    {byUser.length > 0 ? <HBarsLimited items={byUser.map((u) => ({ name: u.name, value: u.value, text: String(u.value) }))} /> : <KitEmptyNote>Nenhuma venda finalizada no período</KitEmptyNote>}
                </ChartCard>
            </>
        );

        const tabsNode = (
            <Box sx={{ mt: '22px' }}>
                <KitSeg
                    value={activeTab}
                    onChange={setActiveTab}
                    options={[{ value: 0, label: 'Lista de vendas' }, { value: 1, label: 'Gráficos' }]}
                />
                <Box sx={{ mt: '14px' }}>
                    {activeTab === 0 ? (
                        <SalesList
                            key={`${sortedSales.length}-${paymentFilter}`}
                            rows={sortedSales}
                            getRow={getListRow}
                            onOpen={(row) => {
                                setSelectedSaleId(row.id);
                                setSaleViewSheetOpen(true);
                            }}
                        />
                    ) : chartsNode}
                </Box>
            </Box>
        );

        return (
            <PageScaffold title="Relatório de Vendas" titlePageTarget="Vendas" links={[{ title: 'Relatórios', link: '/reports' }]}>
                <MobileReport
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    onGenerate={regenerate}
                    onClear={handleClearFilters}
                    exportItems={[{ label: 'Excel (CSV)', icon: protoIcon('download'), onClick: () => handleExport('sales') }]}
                    extraFilters={(
                        <KitField label="Forma de pagamento">
                            <KitSelect value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} aria-label="Forma de pagamento">
                                <option value="">Todos</option>
                                {['DINHEIRO', 'DÉBITO', 'CRÉDITO', 'PIXKEY', 'PIXQRCODE'].map((rawType) => (
                                    <option key={rawType} value={rawType}>{paymentFriendlyLabel(rawType)}</option>
                                ))}
                            </KitSelect>
                        </KitField>
                    )}
                >
                    <MobileReportBody
                        loading={loading}
                        ready={Boolean(reportData)}
                        kpis={[
                            { label: 'Faturamento', icon: 'dollar', grad: true, value: formatMoneyKpi(net) },
                            { label: 'Vendas', icon: 'receipt', value: totalSalesStatusCount.toLocaleString('pt-BR') },
                            { label: 'Ticket médio', icon: 'tag', value: formatMoneyKpi(ticket) },
                            { label: 'Cancelamentos', icon: 'ban', value: cancelledCount.toLocaleString('pt-BR'), delta: { text: cancelledCount > 0 ? `${cancelPct}%` : '', tone: 'down' } },
                        ]}
                        sections={[
                            { type: 'kpis', title: 'Totais do período', items: totalKpis },
                            { type: 'node', title: 'Detalhamento', node: tabsNode },
                        ]}
                    />
                </MobileReport>
                <DialogInfo open={openDialog} onClose={() => setOpenDialog(false)} title="Erro" iconType="error" content={dialogMessage} />
                <SaleViewSheet
                    open={saleViewSheetOpen}
                    onClose={() => {
                        setSaleViewSheetOpen(false);
                        setSelectedSaleId(null);
                    }}
                    saleId={selectedSaleId}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title="Relatório de Vendas"
            titlePageTarget="Vendas"
            links={[{ title: "Relatórios", link: "/reports" }]}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                    {/* page-head — ícone + título + descrição + ação primária
                        (Exportar CSV), igual a prototipo-relatorios.html. */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'flex-end' },
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 2.5,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                width: 38,
                                height: 38,
                                borderRadius: '11px',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                background: (t) => t.palette.gradient.brand,
                            }}>
                                <Receipt size={18} strokeWidth={2} />
                            </Box>
                            <Box>
                                <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                    Relatório de Vendas
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                    Desempenho de vendas por período, forma de pagamento e categoria.
                                </Typography>
                            </Box>
                        </Box>

                    </Box>

                    {/* filter-bar (sem card) — período + ações (Limpar / Exportar / Gerar). */}
                    <ReportFilterBar
                        start={startDate}
                        end={endDate}
                        onPeriodChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                        onGenerate={fetchReportData}
                        loading={loading}
                        onClear={handleClearFilters}
                        exportItems={[{ label: 'Vendas', description: 'Todas as vendas do período (CSV)', onClick: () => handleExport('sales') }]}
                    >
                        {/* Filtro de forma de pagamento — client-side, afeta só a tabela (não exige Gerar). */}
                        <Box>
                            <TbLabel>Forma de pagamento</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 190 }}>
                                <Select
                                    value={paymentFilter}
                                    displayEmpty
                                    onChange={(e) => setPaymentFilter(e.target.value)}
                                    renderValue={(value) => value ? paymentFriendlyLabel(value) : 'Todos'}
                                    sx={selectFieldSx}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {['DINHEIRO', 'DÉBITO', 'CRÉDITO', 'PIXKEY', 'PIXQRCODE'].map((rawType) => (
                                        <MenuItem key={rawType} value={rawType}>
                                            {paymentFriendlyLabel(rawType)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </ReportFilterBar>

                    {/* Chips de filtro ativo (só renderiza com filtro de pagamento ativo). */}
                    {paymentFilter && (
                        <FilterChips sx={{ mb: { xs: 1.5, sm: 3 } }}>
                            <Chip
                                icon={<CreditCard size={13} strokeWidth={2} />}
                                label={`Pagamento: ${paymentFriendlyLabel(paymentFilter)}`}
                                size="small"
                                onDelete={() => setPaymentFilter('')}
                                sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                            />
                        </FilterChips>
                    )}

                    {/* Conteúdo do relatório */}
                    {reportData ? (
                        <>
                            {/* KPIs estilo bolsa de valores (`KpiTickerCard`) — ref.
                                `ui-ux/prototipo-relatorio-vendas-kpis.html`. Grid de 12 colunas:
                                3 largos (4) + 4 médios (3) em lg; 2 por linha em sm; 1 em xs.
                                Descontos e cancelamentos são "ruins quando sobem" (`inverse`).
                                Sem a tendência (falha do endpoint) os cards mostram só o que
                                `/chart/sale` já devolve. */}
                            {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: '10px' }}>
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>Indicadores do período</Typography>
                                {kpiTrend?.days?.length > 0 && (
                                    <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled' }}>
                                        {kpiTrend.days.length} {kpiTrend.days.length === 1 ? 'dia' : 'dias'} · vs. {kpiTrend.days.length} {kpiTrend.days.length === 1 ? 'dia anterior' : 'dias anteriores'}
                                    </Typography>
                                )}
                            </Box> */}
                            {/* <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                                gap: { xs: 1, md: '10px' },
                                mb: '14px',
                                '& > *': { gridColumn: { xs: 'span 12', sm: 'span 6', lg: 'span 3' }, minWidth: 0 },
                                '& > :nth-of-type(-n+3)': { gridColumn: { xs: 'span 12', sm: 'span 6', lg: 'span 4' } },
                                '& > :last-child': { gridColumn: { xs: 'span 12', sm: 'span 12', lg: 'span 3' } },
                            }}>
                                <KpiTickerCard hero cumulative uid="fat" kind="money" label="Faturamento líquido" icon={<Wallet size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" data={kpiTrend?.revenue} days={kpiTrend?.days} value={Math.round(parseMoney(reportData.TotalAmountNet) * 100)} loading={loading} />
                                <KpiTickerCard cumulative uid="qtd" kind="units" label="Vendas realizadas" icon={<ShoppingCart size={11} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" data={kpiTrend?.sales} days={kpiTrend?.days} value={finalizedCount} loading={loading} />
                                <KpiTickerCard uid="tkm" kind="money" label="Ticket médio" icon={<Receipt size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="brand2.main" data={kpiTrend?.ticket} days={kpiTrend?.days} value={finalizedCount > 0 ? Math.round((parseMoney(reportData.TotalAmountNet) * 100) / finalizedCount) : 0} loading={loading} />
                                <KpiTickerCard cumulative uid="itn" kind="units" label="Itens vendidos" icon={<Package size={11} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" data={kpiTrend?.items} days={kpiTrend?.days} loading={loading} />
                                <KpiTickerCard cumulative uid="luc" kind="money" label="Lucro bruto" icon={<TrendingUp size={11} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" data={kpiTrend?.profit} days={kpiTrend?.days} loading={loading}
                                    note={kpiTrend && <>Margem <b>{fmtBp(kpiTrend.margin.total)}</b> · ant. {fmtBp(kpiTrend.margin.previous_total)}</>} />
                                <KpiTickerCard cumulative inverse uid="dsc" kind="money" label="Descontos concedidos" icon={<Percent size={11} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" data={kpiTrend?.discounts} days={kpiTrend?.days} loading={loading}
                                    note={kpiTrend && <><b>{fmtBp(kpiTrend.discount_rate.total)}</b> do valor bruto</>} />
                                <KpiTickerCard cumulative inverse uid="can" kind="units" label="Vendas canceladas" icon={<XCircle size={11} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" data={kpiTrend?.canceled} days={kpiTrend?.days} value={statusDonutData.find((item) => (item.label || '').toLowerCase() === 'cancelada')?.value || 0} loading={loading}
                                    note={kpiTrend && <><b>{fmtBp(kpiTrend.cancel_rate.total)}</b> das vendas · ant. {fmtBp(kpiTrend.cancel_rate.previous_total)}</>} />
                            </Box> */}

                            {/* seg-tabs — Lista de vendas / Gráficos (DESIGN-SYSTEM.md §9
                                "ToggleButtonGroup restyled", já estilizado globalmente em theme.js). */}
                            <Box sx={{ mb: 2.25 }}>
                                <ToggleButtonGroupTabs activeTab={activeTab} onChange={setActiveTab} />
                            </Box>

                            {activeTab === 0 && (
                                <Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr)' }, gap: '14px', mb: '14px' }}>
                                        <GrossNetPanel
                                            loading={loading}
                                            totalSales={tableData.length}
                                            totalGross={parseMoney(reportData.TotalAmountGross)}
                                            totalNet={parseMoney(reportData.TotalAmountNet)}
                                        />
                                        <MixPanel
                                            loading={loading}
                                            items={[
                                                { key: 'cash', label: 'Dinheiro', color: theme.palette.success.main, value: parseMoney(reportData.TotalAmountSaleCash) },
                                                { key: 'debit', label: 'Débito', color: theme.palette.primary.main, value: parseMoney(reportData.TotalAmountSaleDebit) },
                                                { key: 'credit', label: 'Crédito', color: theme.palette.secondary.main, value: parseMoney(reportData.TotalAmountSaleCredit) },
                                                { key: 'pix', label: 'Pix', color: theme.palette.brand2.main, value: parseMoney(reportData.TotalAmountSalePixQrCode) },
                                            ]}
                                        />
                                    </Box>
                                    <DefaultTable
                                        colunms={[
                                            { path: 'number', name: 'Número' },
                                            { path: 'date_created', name: 'Data' },
                                            {
                                                path: 'customer_name',
                                                name: 'Cliente',
                                                render: (row) => row.customer_name || '—',
                                            },
                                            {
                                                path: 'items_gross_subtotal',
                                                name: 'Valor Total',
                                                render: (row) => getSaleRowGrossAmount(row),
                                            },
                                            {
                                                path: 'total_item_discount',
                                                name: 'Desc. itens',
                                                render: (row) => getSaleRowItemDiscount(row),
                                            },
                                            {
                                                path: 'final_total',
                                                name: 'Total c/ Desconto',
                                                render: (row) => getSaleRowPayableTotal(row),
                                            },
                                            {
                                                path: 'type_payment',
                                                name: 'Pagamento',
                                                render: (row) => {
                                                    const paymentTypes = row.type_payment?.split(',').map((t) => t.trim()).filter(Boolean) || [];
                                                    if (paymentTypes.length === 0) {
                                                        return <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>—</Typography>;
                                                    }
                                                    return (
                                                        <Stack direction="column" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                            {paymentTypes.map((type, index) => (
                                                                <PaymentChip key={index} raw={type} />
                                                            ))}
                                                        </Stack>
                                                    );
                                                }
                                            },
                                            {
                                                path: 'status',
                                                name: 'Status',
                                                render: (row) => <StatusChip domain="sale" value={row.status} />
                                            },
                                        ]}
                                        data={tableData}
                                        iconsActions={[{
                                            icon: <Eye size={16} strokeWidth={2} />,
                                            props: {
                                                onClick: (row) => {
                                                    setSelectedSaleId(row.id);
                                                    setSaleViewSheetOpen(true);
                                                },
                                                tooltip: 'Visualizar venda',
                                            }
                                        }]}
                                        rowPerPage={10}
                                        itemLabel="vendas"
                                        sortableFields={['number', 'date_created', 'status']}
                                        defaultSort={{ field: 'number', direction: 'desc' }}
                                    />
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {/* charts-grid — Vendas x Custos / Crescimento mensal (prototipo-relatorios.html). */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                        <SalesVsCostsYearChart
                                            year={salesVsCostsYear}
                                            onYearChange={setSalesVsCostsYear}
                                            payload={salesVsCostsPayload}
                                            loading={salesVsCostsLoading}
                                            errorMessage={salesVsCostsError}
                                        />
                                        <AreaLineChart
                                            dataset={monthlySalesGrowthChartData}
                                            xKey="month"
                                            yKey="total"
                                            gradientId="salesReportMonthlyAreaGradient"
                                            title="Crescimento mensal de vendas"
                                            subtitle={`Ano de ${monthlySalesGrowthYear}`}
                                            loading={monthlySalesGrowthLoading}
                                            errorMessage={monthlySalesGrowthError}
                                            height={340}
                                            headerActions={(
                                                <FormControl size="small" sx={{ minWidth: 96 }}>
                                                    <Select
                                                        value={monthlySalesGrowthYear}
                                                        onChange={(e) => setMonthlySalesGrowthYear(Number(e.target.value))}
                                                        displayEmpty
                                                        sx={selectFieldSx}
                                                    >
                                                        {monthlyGrowthYearOptions.map((y) => (
                                                            <MenuItem key={y} value={y}>{y}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        />
                                    </Box>

                                    {/* Total de vendas por hora — mesma visualização de OrderDashboard.js. */}
                                    <AreaLineChart
                                        dataset={getSalesByHourData()}
                                        xKey="hour"
                                        yKey="count"
                                        gradientId="salesReportHourAreaGradient"
                                        title="Total de Vendas x Hora"
                                        subtitle="Distribuição das vendas ao longo do dia"
                                        loading={loading}
                                        emptySubtitle="Ajuste o período acima para visualizar a distribuição por hora"
                                    />

                                    {/* charts-grid.thirds — Pagamento / Status / Categoria (prototipo-relatorios.html). */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2, alignItems: 'stretch' }}>
                                        <DonutPanel
                                            title="Vendas x forma de pagamento"
                                            data={paymentDonutData}
                                            centerValue={totalPaymentCount.toLocaleString('pt-BR')}
                                            centerCaption="vendas"
                                            loading={loading}
                                            emptyTitle="Nenhum dado disponível"
                                            emptySubtitle="Sem pagamentos no período selecionado"
                                        />
                                        <DonutPanel
                                            title="Finalizadas x Canceladas"
                                            data={statusDonutData}
                                            centerValue={`${finalizedPct}%`}
                                            centerCaption="finalizadas"
                                            loading={loading}
                                            emptyTitle="Nenhum dado disponível"
                                            emptySubtitle="Sem vendas no período selecionado"
                                        />
                                        <CategoryBarChart
                                            data={getSalesByCategoryData()}
                                            xKey="category"
                                            title="Vendas x categoria"
                                            loading={loading}
                                        />
                                    </Box>

                                    {/* Extras existentes (não cobertos pelo protótipo simplificado, mas
                                        mantidos por preservação de comportamento). */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                        <CategoryBarChart
                                            data={getSalesBySubCategoryData()}
                                            xKey="subcategory"
                                            title="Vendas x subcategoria"
                                            loading={loading}
                                        />
                                        <CategoryBarChart
                                            data={getSalesByPaymentTotalData()}
                                            xKey="payment"
                                            title="Total vendas (R$) x forma de pagamento"
                                            loading={loading}
                                            currency
                                        />
                                    </Box>

                                    {/* Gráfico de Vendas por Vendedor — já estava desativado antes
                                        deste redesign, mantido comentado por preservação de comportamento. */}
                                    {/* <CategoryBarChart
                                        data={getSalesByUserData()}
                                        xKey="user"
                                        title="Quantidade de Vendas x Vendedor"
                                        loading={loading}
                                    /> */}
                                </Box>
                            )}
                        </>
                    ) : (
                        <Card>
                            <CardContent sx={{ py: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: '50%',
                                    border: '2px dashed',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'text.disabled',
                                }}>
                                    <Receipt size={30} strokeWidth={2} />
                                </Box>
                                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.secondary' }}>
                                    Nenhum relatório gerado
                                </Typography>
                                <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', maxWidth: 380 }}>
                                    Selecione um período e clique em "Gerar relatório" para visualizar os dados de vendas.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </LocalizationProvider>

            <DialogInfo
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                title="Erro"
                iconType="error"
                content={dialogMessage}
            />

            <SaleViewSheet
                open={saleViewSheetOpen}
                onClose={() => {
                    setSaleViewSheetOpen(false);
                    setSelectedSaleId(null);
                }}
                saleId={selectedSaleId}
            />
        </PageScaffold>
    );
}

// seg-tabs — `ToggleButtonGroup` restyled globalmente em `theme.js` (pill
// track + aba selecionada elevada, DESIGN-SYSTEM.md §9), mas essa tela tem
// protótipo dedicado com medidas próprias (`.seg-tabs`/`.seg-tabs button` em
// `prototipo-relatorios.html`/`design-system.html`: track 4px de padding/gap
// e 11px de raio, botão 9px/18px de padding, 8px de raio, 12.5px/700 de
// fonte) — mais apertado que o default global (2px/10px), então é
// sobrescrito aqui por instância em vez de mudar o tema inteiro.
// Botões individuais dentro do `ToggleButtonGroup` — dois ajustes além das
// medidas de padding/fonte: (1) `lineHeight` normal, porque a variante de
// tipografia `button` do tema usa `lineHeight: 1.75` (herdado do default do
// MUI), o que sozinho já deixa o botão ~7px mais alto que os "9px/18px de
// padding + fonte 12.5px" do protótipo; (2) `!important` no raio, porque o
// `ToggleButtonGroup` zera o raio dos lados "internos" de cada botão
// (`:not(:first-of-type)`/`:not(:last-of-type)`) para simular um controle
// segmentado unido — no protótipo cada botão é uma pill 100% arredondada
// dentro da trilha, não um controle unido.
const segTabButtonSx = {
    gap: '7px',
    padding: '9px 18px',
    lineHeight: 'normal',
    borderRadius: '8px !important',
    fontSize: '12.5px',
    fontWeight: 700,
};

function ToggleButtonGroupTabs({ activeTab, onChange }) {
    return (
        <ToggleButtonGroup
            exclusive
            value={activeTab}
            onChange={(e, v) => (v || v === 0) && onChange(v)}
            sx={{ width: 'fit-content', p: '4px', gap: '4px', borderRadius: '11px' }}
        >
            <ToggleButton value={0} sx={segTabButtonSx}>
                <ListIcon size={14} strokeWidth={2} /> Lista de vendas
            </ToggleButton>
            <ToggleButton value={1} sx={segTabButtonSx}>
                <BarChart3 size={14} strokeWidth={2} /> Gráficos
            </ToggleButton>
        </ToggleButtonGroup>
    );
}
